import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { EnvelopeSimple, ArrowRight, ArrowLeft, CheckCircle, WarningCircle } from '@phosphor-icons/react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ecowater-cloud/artifacts/tzhqe5c4_ECOWATER_WORDMARK_LOGO.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const val = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) { setError('Please enter a valid email address'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: val });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testid="forgot-password-page">
      {sent ? (
        <div className="text-center" data-testid="forgot-success">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#DCEBE2] flex items-center justify-center">
            <CheckCircle size={30} weight="fill" className="text-[#2F6D4B]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Check your email</h2>
          <p className="text-sm text-gray-500 mt-2">If that email is registered, we've sent a recovery link. It expires in 15 minutes.</p>
          <p className="text-xs text-gray-400 mt-3">Didn't get it? Check your spam folder, or try again in a couple of minutes.</p>
          <Link to="/login" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0A2540] hover:underline" data-testid="back-to-login">
            <ArrowLeft size={15} /> Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <div className="text-center mb-7">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Forgot password?</h2>
            <p className="text-sm text-gray-400 mt-1">Enter your registered email and we'll send you a recovery link.</p>
          </div>
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2.5 rounded-lg mb-5" data-testid="forgot-error">
              <WarningCircle size={16} /><span className="text-xs">{error}</span>
            </div>
          )}
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Email address</label>
              <div className="relative">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required data-testid="forgot-email-input"
                  className="w-full h-11 pl-10 pr-3 rounded-md border border-gray-200 bg-transparent text-gray-900 text-sm shadow-sm placeholder:text-gray-300 focus:outline-none focus:border-[#0A2540] focus:ring-1 focus:ring-[#0A2540]/20 transition-colors"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
              className="w-full h-11 rounded-md text-white font-semibold text-sm transition-all duration-200 hover:shadow-lg hover:shadow-[#0A2540]/20 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
              {loading ? (<><span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>) : (<>Send recovery link<ArrowRight size={16} /></>)}
            </button>
          </form>
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:underline" data-testid="back-to-login">
              <ArrowLeft size={15} /> Back to sign in
            </Link>
          </div>
        </>
      )}
    </AuthShell>
  );
}

export function AuthShell({ children, testid }) {
  return (
    <div className="min-h-screen flex items-center justify-center relative bg-[#fafbfd] overflow-hidden" data-testid={testid}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(7,121,188,0.08) 0%, transparent 70%)' }} />
      <div className="w-full max-w-[400px] mx-6">
        <div className="mb-8 flex justify-center">
          <div className="inline-flex flex-col items-end leading-none">
            <img src={LOGO_URL} alt="Ecowater" className="h-7 w-auto" />
            <span className="text-[12px] font-semibold tracking-[0.18em] text-[#0A2540] mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>CLOUD</span>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_8px_40px_rgba(7,121,188,0.06)] p-8">
          {children}
        </div>
        <p className="text-center text-[10px] text-gray-300 mt-6 tracking-wider uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          v1.0 &bull; 2026 Ecowater Cloud
        </p>
      </div>
    </div>
  );
}
